import type { ServicePageConfig } from "../../../types";
import {
  getPackagePricingRows,
  PACKAGE_PRICE_HINT_SV,
  PACKAGE_PRICE_HINT_EN,
} from "../../../package-pricing";
import { getCityMunicipalityList } from "../../../cities";

const goteborgKommunerText = getCityMunicipalityList("göteborg");

// ─── Flytthjälp Mölndal ────────────────────────────────────────────
export const flytthjalpMolndalPage: ServicePageConfig = {
  slug: "flytthjalp-molndal",
  serviceCategory: "flytt",
  city: "göteborg",

  meta: {
    title: "Flytthjälp Mölndal – Bärare och Skåpbil | Ungdomskraft",
    description:
      "Flytthjälp i Mölndal, Kållered och Lindome. Vi bär, lastar och kör – skåpbil och personal ingår. Se priset direkt, RUT dras på fakturan. Ofta lediga tider.",
    h1: "Flytthjälp Mölndal",
  },

  hero: {
    heading: "Flytthjälp i Mölndal",
    intro:
      "Ska du flytta i Mölndal? Vi skickar bärare och skåpbil till lägenheten vid Mölndalsbro, huset i Toltorpsdalen, radhuset i Kållered eller villan i Lindome – och kör dit du ska, i kommunen, in till Göteborg eller längre bort. Du bokar online, ser priset innan du bekräftar och får RUT-avdraget direkt på fakturan.",
    heroImage: "/hero-flytthjalp.png",
    heroImageAlt:
      "Ungdomskraft bär möbler till skåpbil vid flytthjälp i Mölndal",
  },

  featureCards: {
    sinceText: "Flytthjälp sedan 2014",
    storyText:
      "Sedan 2014 har Ungdomskraft flyttat hushåll kors och tvärs i Göteborgsregionen. Mölndal ligger vägg i vägg med Göteborg, så vi rör oss här varje vecka – i trapphusen kring centrum, i villagatorna i Toltorpsdalen och ute i Kållered och Lindome.",
    bookYourText: "Boka på några minuter",
    bookDescText:
      "Ange nuvarande och ny adress, lista möblerna och välj dag. Priset räknas ut direkt – ingen offert behövs.",
    vehicleTitleText: "Bil, bärare och utrustning",
    vehicleDescText:
      "Skåpbil, möbelfiltar, bärremmar och spännband följer med. Du behöver varken låna bil eller be vänner ställa upp en lördag.",
  },

  itemsSection: {
    heading: "Möbler och saker vi flyttar i Mölndal",
    items: [
      {
        name: "Soffor",
        description: "Tresitssoffor, hörnsoffor och schäslonger som behöver vinklas genom trapphuset",
        image: "/sofa.webp",
        alt: "Hörnsoffa som bärs vid flytthjälp i Mölndal",
      },
      {
        name: "Sängar",
        description: "Dubbelsängar, ramsängar och tunga ställbara sängar",
        image: "/bed.webp",
        alt: "Dubbelsäng som flyttas från lägenhet i Mölndal centrum",
      },
      {
        name: "Bord",
        description: "Matbord, soffbord och skrivbord från hemmakontoret",
        image: "/table.webp",
        alt: "Matbord som lastas i skåpbil vid flytt i Kållered, Mölndal",
      },
      {
        name: "Vitvaror",
        description: "Tvättmaskin, torktumlare och fristående kyl och frys",
        image: "/washer.webp",
        alt: "Tvättmaskin som bärs vid flytt i Lindome, Mölndal",
      },
      {
        name: "Förvaring",
        description: "Garderobsstommar, byråer, vitrinskåp och bokhyllor",
        image: "/wardrobe.webp",
        alt: "Garderob som flyttas från radhus i Toltorpsdalen, Mölndal",
      },
      {
        name: "Kartonger",
        description: "Packade flyttlådor, källarförråd och saker från vinden",
        image: "/box.webp",
        alt: "Flyttkartonger som lastas vid flytthjälp i Krokslätt, Mölndal",
      },
    ],
  },

  seoContent: {
    mainHeading: "Flytthjälp i Mölndal – nära Göteborg, med egna förutsättningar",
    mainText:
      "Mölndal har runt 72 000 invånare och gränsar direkt mot Göteborg i norr. Staden har förändrats mycket de senaste åren: Mölndals centrum har byggts om med nya bostadskvarter kring Mölndalsbro, och i Krokslätt och Fässberg finns både äldre hyreshus och nyproducerade bostadsrätter. Längre bort från centrum tar villorna över – Toltorpsdalen är till största delen byggd med småhus, och i Kållered och Lindome söderut bor många i radhus och villor med egen tomt. En flytt i Mölndal kan därför betyda allt från en trång trappa i ett äldre hus till ett garage fullt av cyklar och trädgårdsverktyg.\n\nEftersom Mölndal ligger så nära Göteborg är många flyttar korta i kilometer men långa i bärtid. Den typiska kunden flyttar från en lägenhet i Göteborg till ett radhus i Kållered, eller tvärtom – barnen har flyttat ut och villan i Lindome byts mot en lägenhet med hiss nära spårvagnen i Mölndal centrum. Sträckan ryms oftast inom de 20 km som ingår i paketet, så priset styrs mest av hur mycket som ska bäras och hur lång tid det tar.\n\nVi kör via E6/E20, som går rakt genom kommunen, och Rv 27/40 för flyttar österut. Pendeltåget mot Kungsbacka stannar vid Mölndal, Kållered och Lindome, och spårvagnarna går till knutpunkten Mölndalsbro – bra att veta om du vill åka före till nya bostaden medan vi kör bohaget. I centrum och vid köpcentret kring Mölndalsbro är lastplatserna få, så berätta gärna vid bokningen var vi kan stanna nära porten.",
    preparationHeading: "Checklista inför flytten i Mölndal",
    preparationText:
      "Börja med förråden – källarförråd i flerbostadshus och garaget i villan är det som oftast glöms bort tills flyttdagen. Bestäm vad som ska med och vad som kan slängas. Packa sedan rum för rum och märk varje kartong. Ställ färdigpackade lådor nära ytterdörren så går utbäringen snabbare.\n\nFlyttar du i centrala Mölndal eller Krokslätt: kolla med hyresvärden eller föreningen om hissen kan låsas för flytten och om det finns en lastzon. Flyttar du från villa i Toltorpsdalen, Kållered eller Lindome: se till att uppfarten är tom så skåpbilen kan stå nära. Frosta av frysen dagen innan, töm vattnet ur tvättmaskinen och ha nycklar och koder till båda adresserna nära till hands.",
    additionalSections: [
      {
        heading: "Flytt mellan Göteborg och Mölndal",
        content:
          "Gränsen mellan Göteborg och Mölndal märks knappt när man kör – Krokslätt och Johanneberg går nästan ihop. Därför är flyttar mellan de två städerna några av de vanligaste vi gör. Vi bär ner i Göteborg, kör en kort bit söderut och bär upp i Mölndal på samma förmiddag. Är bohaget stort gör vi flera vändor, och eftersom sträckan är kort går det snabbt.",
        cta: { text: "Se flytthjälp i Göteborg →", href: "/flytthjalp-goteborg" },
      },
      {
        heading: "Områden vi flyttar i inom Mölndals kommun",
        content:
          "Vi flyttar i hela Mölndal: centrum och Mölndalsbro, Krokslätt, Fässberg, Kvarnbyn vid Mölndalsån, Åby, Balltorp, Toltorpsdalen, Bifrost, Eklanda och Lackarebäck. Söderut tar vi flyttar i Kållered och Lindome, inklusive villaområdena längs E6.\n\nFlytthjälp är RUT-berättigad. Du får 50 % avdrag på arbetskostnaden, högst 75 000 kr per person och år, och avdraget görs direkt på fakturan.",
      },
      {
        heading: "Varför boka flytthjälp i Mölndal med Ungdomskraft?",
        listItems: [
          {
            label: "Kort framkörning –",
            text: "Mölndal ligger mitt i vårt område i Göteborgsregionen. Det gör att vi ofta har en bil i närheten och kan ta flytten med kort varsel.",
          },
          {
            label: "Vana vid båda sorternas flytt –",
            text: "Hyreslägenhet i centrum eller villa i Lindome – vi anpassar antalet bärare och vändor efter bohaget.",
          },
          {
            label: "Priset står i bokningen –",
            text: "Du ser vad flytten kostar innan du bekräftar. Inga timmar som tickar och ingen faktura som överraskar.",
          },
          {
            label: "Trygg Hansa –",
            text: "Flytten är försäkrad från start till mål, både för dina saker och för trapphus och väggar.",
          },
          {
            label: "Montering ingår –",
            text: "Vi skruvar isär det som behövs för att få ut möblerna och sätter ihop dem igen i nya bostaden.",
          },
        ],
      },
    ],
  },

  faq: [
    {
      question: "Vad kostar flytthjälp i Mölndal?",
      answer:
        "Priset följer våra paket. Enstaka tunga möbler kan rymmas i Liten (995 kr) eller Mellan (1 495 kr), en mindre lägenhet brukar bli Extra Stor (4 995 kr, upp till 4 timmar) och en villa eller stor lägenhet Mega Stor (8 995 kr, upp till 8 timmar). De flesta flyttar inom Mölndal och in till Göteborg ryms i de 20 km som ingår.",
    },
    {
      question: "Kan jag få RUT-avdrag på flytthjälp i Mölndal?",
      answer:
        "Ja, flytthjälp ger rätt till RUT-avdrag med 50 % av arbetskostnaden, upp till 75 000 kr per person och år. Vi sköter avdraget åt dig direkt på fakturan.",
    },
    {
      question: "Hur snabbt kan ni flytta mig i Mölndal?",
      answer:
        "Ofta samma vecka och ibland samma dag. Mölndal ligger nära våra andra uppdrag i Göteborg, så vi har ofta en bil i närheten. Lediga tider syns direkt i bokningen.",
    },
    {
      question: "Flyttar ni från Göteborg till Mölndal?",
      answer:
        "Ja, det är en av våra vanligaste sträckor. Från till exempel Johanneberg eller Guldheden till Mölndal centrum är det bara några kilometer, och körningen ingår i paketet.",
    },
    {
      question: "Hjälper ni till i Kållered och Lindome?",
      answer:
        "Ja, vi flyttar i hela kommunen, även i Kållered och Lindome i söder. Där handlar det oftast om radhus och villor med förråd och garage, och vi gör flera vändor om det behövs.",
    },
    {
      question: "Kan ni flytta till en lägenhet utan hiss i Mölndal?",
      answer:
        "Ja, vi bär i trapphus utan hiss utan tillägg. Säg till i bokningen vilken våning det gäller så skickar vi rätt antal bärare.",
    },
    {
      question: "Var parkerar skåpbilen i centrala Mölndal?",
      answer:
        "Vi försöker alltid stå så nära porten som möjligt. I nybyggda kvarter kring Mölndalsbro finns ofta en lastplats eller ett garage med infart – berätta vid bokningen om du vet var vi kan stanna.",
    },
    {
      question: "Ingår skåpbil?",
      answer:
        "Ja, skåpbil och personal ingår alltid. Du behöver inte hyra något själv.",
    },
    {
      question: "Kopplar ni loss tvättmaskin och diskmaskin?",
      answer:
        "Vi flyttar alla vitvaror och kan koppla loss och ansluta tvättmaskin och liknande på nya adressen. Töm vattnet ur maskinen och frosta av frysen dagen innan.",
    },
    {
      question: "Flyttar ni på helger i Mölndal?",
      answer:
        "Ja, vi jobbar alla dagar i veckan, även helger. Lördagar är populära, så boka gärna i god tid.",
    },
    {
      question: "Är mina saker försäkrade under flytten?",
      answer:
        "Ja, alla flyttar är försäkrade genom Trygg Hansa.",
    },
    {
      question: "Hur många bärare kommer?",
      answer:
        "Oftast två personer med skåpbil. Är bohaget stort gör vi fler vändor eller planerar en längre arbetsdag – det syns i paketet du väljer.",
    },
    {
      question: "Vilka områden i Mölndal täcker ni?",
      answer:
        "Hela kommunen: centrum, Krokslätt, Fässberg, Kvarnbyn, Åby, Balltorp, Toltorpsdalen, Bifrost, Eklanda, Lackarebäck, Kållered och Lindome.",
    },
    {
      question: "Vilka kommuner kring Mölndal flyttar ni i?",
      answer: `Hela Göteborgsregionen: ${goteborgKommunerText}. Du kan flytta mellan kommunerna i samma bokning.`,
    },
  ],

  priceHint: PACKAGE_PRICE_HINT_SV.transport,

  pricingRows: getPackagePricingRows("transport", "sv"),

  dynamicHeadings: {
    threeSteps: "Så går flytthjälpen i Mölndal till",
    step2Text:
      "Bärarna tar ut allt från gamla bostaden, lastar skåpbilen med filtar mellan möblerna och kör till den nya adressen i Mölndal eller var du nu ska bo.",
    step3Title: "Inburet och uppställt",
    step3Desc:
      "Vi bär in varje möbel och kartong till rätt rum och monterar det vi tog isär. Sen är det bara att börja packa upp.",
    ctaHeading: "Boka flytthjälp i Mölndal",
    ctaText:
      "Skriv in adresserna och vad som ska flyttas. Du ser priset direkt och kan boka med en gång.",
  },

  en: {
    slug: "moving-help-molndal",
    meta: {
      title: "Moving Help Mölndal – Movers and Van | Ungdomskraft",
      description:
        "Moving help in Mölndal, Kållered and Lindome. We carry, load and drive – van and crew included. See the price instantly, RUT taken off the invoice.",
      h1: "Moving Help Mölndal",
    },
    hero: {
      heading: "Moving Help in Mölndal",
      intro:
        "Moving in Mölndal? We send carriers and a van to the flat near Mölndalsbro, the house in Toltorpsdalen, the terraced house in Kållered or the villa in Lindome – and drive wherever you are going, within the municipality, into Gothenburg or further. You book online, see the price before you confirm and get the RUT deduction straight on the invoice.",
      heroImageAlt: "Ungdomskraft carrying furniture to a van during moving help in Mölndal",
    },
    featureCards: {
      sinceText: "Moving help since 2014",
      storyText:
        "Since 2014 Ungdomskraft has moved households all over the Gothenburg region. Mölndal sits right next to Gothenburg, so we are here every week – in the stairwells around the centre, on the residential streets of Toltorpsdalen and out in Kållered and Lindome.",
      bookYourText: "Book in a few minutes",
      bookDescText:
        "Enter your current and new address, list the furniture and choose a day. The price is calculated instantly – no quote needed.",
      vehicleTitleText: "Van, carriers and equipment",
      vehicleDescText:
        "The van, furniture blankets, carrying straps and ratchet straps come along. No need to borrow a car or ask friends to give up a Saturday.",
    },
    itemsSection: {
      heading: "Furniture and items we move in Mölndal",
      items: [
        {
          name: "Sofas",
          description: "Three-seaters, corner sofas and chaises that need angling through the stairwell",
          image: "/sofa.webp",
          alt: "Corner sofa carried during moving help in Mölndal",
        },
        {
          name: "Beds",
          description: "Double beds, bed frames and heavy adjustable beds",
          image: "/bed.webp",
          alt: "Double bed moved from a flat in Mölndal centrum",
        },
        {
          name: "Tables",
          description: "Dining tables, coffee tables and desks from the home office",
          image: "/table.webp",
          alt: "Dining table loaded into a van during a move in Kållered, Mölndal",
        },
        {
          name: "Appliances",
          description: "Washing machines, tumble dryers and free-standing fridges and freezers",
          image: "/washer.webp",
          alt: "Washing machine carried during a move in Lindome, Mölndal",
        },
        {
          name: "Storage",
          description: "Wardrobe frames, dressers, display cabinets and bookcases",
          image: "/wardrobe.webp",
          alt: "Wardrobe moved from a terraced house in Toltorpsdalen, Mölndal",
        },
        {
          name: "Boxes",
          description: "Packed moving boxes, basement storage and things from the attic",
          image: "/box.webp",
          alt: "Moving boxes loaded during moving help in Krokslätt, Mölndal",
        },
      ],
    },
    seoContent: {
      mainHeading: "Moving help in Mölndal – close to Gothenburg, with its own conditions",
      mainText:
        "Mölndal has around 72,000 residents and borders Gothenburg directly to the north. The city has changed a lot in recent years: Mölndal centrum has been rebuilt with new residential blocks around Mölndalsbro, and Krokslätt and Fässberg have both older rental buildings and newly built owner-occupied flats. Further from the centre, houses take over – Toltorpsdalen is mostly built with detached houses, and in Kållered and Lindome to the south many live in terraced houses and villas with their own plot. A move in Mölndal can therefore mean anything from a narrow staircase in an older building to a garage full of bikes and garden tools.\n\nBecause Mölndal is so close to Gothenburg, many moves are short in kilometres but long in carrying time. The typical customer moves from a flat in Gothenburg to a terraced house in Kållered, or the other way round – the children have moved out and the villa in Lindome is swapped for a flat with a lift near the tram in Mölndal centrum. The distance usually fits within the 20 km included in the package, so the price is mostly about how much there is to carry and how long it takes.\n\nWe drive via the E6/E20, which runs straight through the municipality, and routes 27/40 for moves to the east. The commuter train towards Kungsbacka stops at Mölndal, Kållered and Lindome, and the trams run to the Mölndalsbro interchange – useful if you want to go ahead to the new home while we drive the household. In the centre and around the shopping centre at Mölndalsbro loading spots are few, so tell us when booking where we can stop near the door.",
      preparationHeading: "Checklist before your move in Mölndal",
      preparationText:
        "Start with storage – basement storage rooms in apartment buildings and the garage at a house are what most often gets forgotten until moving day. Decide what is coming and what can be thrown away. Then pack room by room and label every box. Put finished boxes near the front door so carrying out goes faster.\n\nMoving in central Mölndal or Krokslätt: check with the landlord or housing association whether the lift can be locked for the move and whether there is a loading zone. Moving from a house in Toltorpsdalen, Kållered or Lindome: make sure the driveway is empty so the van can park close. Defrost the freezer the day before, drain the washing machine and keep keys and codes for both addresses to hand.",
      additionalSections: [
        {
          heading: "Moving between Gothenburg and Mölndal",
          content:
            "You hardly notice the border between Gothenburg and Mölndal when driving – Krokslätt and Johanneberg almost merge. That is why moves between the two cities are some of the most common we do. We carry down in Gothenburg, drive a short way south and carry up in Mölndal in the same morning. If the household is large we make several trips, and since the distance is short it goes quickly.",
          cta: { text: "See moving help in Gothenburg →", href: "/en/moving-help-gothenburg" },
        },
        {
          heading: "Areas we move in within Mölndal municipality",
          content:
            "We move across all of Mölndal: the centre and Mölndalsbro, Krokslätt, Fässberg, Kvarnbyn by the Mölndalsån river, Åby, Balltorp, Toltorpsdalen, Bifrost, Eklanda and Lackarebäck. To the south we take moves in Kållered and Lindome, including the residential areas along the E6.\n\nMoving help qualifies for RUT. You get 50% off the labour cost, at most SEK 75,000 per person per year, and the deduction is made directly on the invoice.",
        },
        {
          heading: "Why book moving help in Mölndal with Ungdomskraft?",
          listItems: [
            {
              label: "Short drive to you –",
              text: "Mölndal is in the middle of our area in the Gothenburg region. That means we often have a van nearby and can take the move at short notice.",
            },
            {
              label: "Used to both kinds of move –",
              text: "Rental flat in the centre or house in Lindome – we adapt the number of carriers and trips to the household.",
            },
            {
              label: "The price is in the booking –",
              text: "You see what the move costs before you confirm. No hours ticking and no invoice that surprises you.",
            },
            {
              label: "Trygg Hansa –",
              text: "The move is insured from start to finish, both your belongings and the stairwell and walls.",
            },
            {
              label: "Assembly included –",
              text: "We unscrew what is needed to get the furniture out and put it back together in the new home.",
            },
          ],
        },
      ],
    },
    faq: [
      {
        question: "How much does moving help in Mölndal cost?",
        answer:
          "The price follows our packages. A few heavy pieces can fit in Small (SEK 995) or Medium (SEK 1,495), a smaller flat usually becomes Extra Large (SEK 4,995, up to 4 hours) and a house or large flat Mega Large (SEK 8,995, up to 8 hours). Most moves within Mölndal and into Gothenburg fit within the 20 km included.",
      },
      {
        question: "Can I get the RUT deduction on moving help in Mölndal?",
        answer:
          "Yes, moving help qualifies for RUT at 50% of the labour cost, up to SEK 75,000 per person per year. We handle the deduction for you directly on the invoice.",
      },
      {
        question: "How quickly can you move me in Mölndal?",
        answer:
          "Often the same week and sometimes the same day. Mölndal is close to our other jobs in Gothenburg, so we often have a van nearby. Available times show instantly in the booking.",
      },
      {
        question: "Do you move from Gothenburg to Mölndal?",
        answer:
          "Yes, it is one of our most common routes. From Johanneberg or Guldheden to Mölndal centrum, for example, it is only a few kilometres, and the drive is included in the package.",
      },
      {
        question: "Do you help in Kållered and Lindome?",
        answer:
          "Yes, we move across the whole municipality, including Kållered and Lindome in the south. There it is usually terraced houses and villas with sheds and garages, and we make several trips if needed.",
      },
      {
        question: "Can you move me into a flat without a lift in Mölndal?",
        answer:
          "Yes, we carry in stairwells without a lift at no extra charge. Tell us the floor in the booking so we send the right number of carriers.",
      },
      {
        question: "Where does the van park in central Mölndal?",
        answer:
          "We always try to park as close to the door as possible. The new blocks around Mölndalsbro often have a loading spot or a garage entrance – tell us when booking if you know where we can stop.",
      },
      {
        question: "Is a van included?",
        answer: "Yes, a van and crew are always included. You don't need to rent anything yourself.",
      },
      {
        question: "Do you disconnect washing machines and dishwashers?",
        answer:
          "We move all appliances and can disconnect and reconnect a washing machine and similar at the new address. Drain the machine and defrost the freezer the day before.",
      },
      {
        question: "Do you move at weekends in Mölndal?",
        answer:
          "Yes, we work every day of the week, including weekends. Saturdays are popular, so book in good time.",
      },
      {
        question: "Are my belongings insured during the move?",
        answer: "Yes, every move is insured through Trygg Hansa.",
      },
      {
        question: "How many carriers come?",
        answer:
          "Usually two people with a van. If the household is large we make more trips or plan a longer working day – it shows in the package you choose.",
      },
      {
        question: "Which areas in Mölndal do you cover?",
        answer:
          "The whole municipality: the centre, Krokslätt, Fässberg, Kvarnbyn, Åby, Balltorp, Toltorpsdalen, Bifrost, Eklanda, Lackarebäck, Kållered and Lindome.",
      },
      {
        question: "Which municipalities around Mölndal do you move in?",
        answer: `The whole Gothenburg region: ${goteborgKommunerText}. You can move between municipalities in the same booking.`,
      },
    ],
    priceHint: PACKAGE_PRICE_HINT_EN.transport,
    pricingRows: getPackagePricingRows("transport", "en"),
    dynamicHeadings: {
      threeSteps: "How moving help in Mölndal works",
      step2Text:
        "The carriers take everything out of your old home, load the van with blankets between the furniture and drive to the new address in Mölndal or wherever you are going to live.",
      step3Title: "Carried in and set up",
      step3Desc:
        "We carry every piece of furniture and box to the right room and reassemble what we took apart. Then all that's left is unpacking.",
      ctaHeading: "Book moving help in Mölndal",
      ctaText:
        "Enter the addresses and what needs moving. You see the price instantly and can book right away.",
    },
  },
};
